import { defaultShapeUtils } from "tldraw";
import { ET_CANVAS_SHAPE_UTILS } from "@ever-tutor/et-ui";
import { InkDrawShapeUtil } from "@/canvas/ink/InkDrawShapeUtil";
import { SlideEmbedShapeUtil } from "./shapes/SlideEmbedShapeUtil";
import { SpaceTLDrawToolbar } from "./components/SpaceTLDrawToolbar";
import { CanvasMenuPanel } from "@/canvas/components/CanvasPanels";
import type { AiMode, WriteMode, CollabMode } from "./store/useSpaceStore";

// Shared ET canvas vocabulary (math, custom-geo, rich-text) + Spaces-local
// extras. custom-geo must be registered here too: the shared converter can
// emit it, and an unregistered shape type crashes the canvas. The ink util
// stands in for the stock draw util, so a Space smooths student ink the way
// the notepad does. A type listed twice throws at store creation, so every
// stock util that one of these replaces is filtered out.
const REPLACING_UTILS = [...ET_CANVAS_SHAPE_UTILS, InkDrawShapeUtil];
const replacedTypes = new Set<string>(REPLACING_UTILS.map((u) => u.type));
export const SPACES_SHAPE_UTILS = [
  ...defaultShapeUtils.filter((util) => !replacedTypes.has(util.type)),
  ...REPLACING_UTILS,
  SlideEmbedShapeUtil,
];

export const SPACES_COMPONENTS_BASE = {
  MainMenu: null,
  PageMenu: null,
  ActionsMenu: null,
  Minimap: null,
  // SharePanel hidden — participant presence is shown in ParticipantPanel instead
  SharePanel: null,
  Toolbar: SpaceTLDrawToolbar,
  QuickActions: CanvasMenuPanel,
  NavigationPanel: null,
} as const;

export const AI_MODE_STUDENT_TOAST: Record<AiMode, string> = {
  off: "Your teacher turned the AI off.",
  "text-only": "Your teacher set the AI to chat only.",
  voice: "Your teacher turned voice on for the AI.",
};

export const WRITE_MODE_STUDENT_TOAST: Record<WriteMode, string> = {
  read: "Your teacher paused drawing for you.",
  edit: "Your teacher enabled drawing for you again.",
};

export const COLLAB_MODE_STUDENT_TOAST: Record<CollabMode, string> = {
  local: "Your teacher switched to personal canvas mode.",
  shared: "Your teacher switched to shared canvas mode.",
};

// Layer key stamped on every shape's meta. Determines which sync room owns the
// shape and whether the student can mutate it.
//   "shared"  — teacher-drawn content in the shared room (read-only for students)
//   userId    — personal content belonging to a specific student
export type ShapeLayer = "shared" | string;
export const SHARED_LAYER = "shared" as const;

// Asset upload is not supported in spaces — all content is embedded via slide HTML.
export const noopAssets = {
  async upload(_asset: unknown, _file: File): Promise<{ src: string }> {
    throw new Error("Asset upload not supported");
  },
  async resolve(_asset: unknown): Promise<string | null> {
    return null;
  },
};
